// frontend/src/pages/SettingsPage/routes/SettingsIntegrationCategoryRoute.tsx
import { ArrowLeft } from "lucide-react";
import React, { Suspense } from "react";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import { Navigate, useNavigate, useParams } from "react-router";
import { getCategoryBySlug } from "src/components/integrations/integration-categories";
import { RequireScope } from "src/components/RequireScope";
import CustomMcpServersSection from "src/pages/SettingsPage/components/CustomMcpServersSection";

import { SettingsIntegrationCategoryContent_organization$key } from "../components/__generated__/SettingsIntegrationCategoryContent_organization.graphql";
import SettingsIntegrationCategoryContent from "../components/SettingsIntegrationCategoryContent";
import { useSettingsMobileHeader } from "../hooks/useSettingsMobileHeader";
import { SettingsIntegrationCategoryRouteQuery } from "./__generated__/SettingsIntegrationCategoryRouteQuery.graphql";

const settingsIntegrationCategoryRouteQuery = graphql`
  query SettingsIntegrationCategoryRouteQuery {
    me {
      ...appMe
      organization {
        ...SettingsIntegrationCategoryRoute_organization
      }
    }
  }
`;

const settingsIntegrationCategoryRouteFragment = graphql`
  fragment SettingsIntegrationCategoryRoute_organization on GraphQLOrganization
  @refetchable(queryName: "SettingsIntegrationCategoryRouteRefetchQuery") {
    ...SettingsIntegrationCategoryContent_organization
  }
`;

export default function SettingsIntegrationCategoryRoute() {
  return (
    <RequireScope scope="read:integrations">
      <SettingsIntegrationCategoryRouteContent />
    </RequireScope>
  );
}

function McpServersPage() {
  const navigate = useNavigate();
  useSettingsMobileHeader("Custom MCP Servers");

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 md:px-5 pt-4 pb-2">
        <button
          onClick={() => navigate("/settings/integrations")}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Return to Integrations
        </button>

        <h1 className="text-xl font-semibold text-foreground">
          Custom MCP Servers
        </h1>
      </div>
      <div className="flex-grow overflow-auto custom-scrollbar px-4 md:px-5 pb-4">
        <div className="max-w-5xl">
          <Suspense fallback={null}>
            <CustomMcpServersSection />
          </Suspense>
        </div>
      </div>
    </div>
  );
}

function StandardCategoryPage({ categorySlug }: { categorySlug?: string }) {
  const category = categorySlug ? getCategoryBySlug(categorySlug) : undefined;

  useSettingsMobileHeader(category?.title || "Integrations");

  const data = useLazyLoadQuery<SettingsIntegrationCategoryRouteQuery>(
    settingsIntegrationCategoryRouteQuery,
    {},
    { fetchPolicy: "store-or-network" }
  );

  const organizationData = data.me?.organization;
  const [organization, refetch] = useRefetchableFragment(
    settingsIntegrationCategoryRouteFragment,
    organizationData
  );

  if (!category) {
    return <Navigate to="/settings/integrations" replace />;
  }

  if (!organization) {
    return null;
  }

  const handleRefetch = () => {
    refetch({}, { fetchPolicy: "network-only" });
  };

  return (
    <SettingsIntegrationCategoryContent
      category={category}
      organization={organization as SettingsIntegrationCategoryContent_organization$key}
      onRefetch={handleRefetch}
    />
  );
}

function SettingsIntegrationCategoryRouteContent() {
  const { categorySlug } = useParams<{ categorySlug: string }>();

  if (categorySlug === "mcp-servers") {
    return <McpServersPage />;
  }

  return <StandardCategoryPage categorySlug={categorySlug} />;
}
